/*
	wallet_crypto.js — Argon2id + AES-GCM envelope for mnemonic encryption.

	This module replaces the old CryptoJS.AES.encrypt(text, password) scheme
	(which used EVP_BytesToKey / MD5 / 1 iteration / no MAC) with a modern
	envelope:
		- KDF:  Argon2id (via hash-wasm, pure WASM, no native bindings)
		- Cipher: AES-GCM (via Web Crypto API, available in browsers and
		  Electron's renderer)
		- Auth: GCM tag provides tamper detection and wrong-password detection.

	Blob/file discriminator: a string field `kdf`. Old blobs/files have no
	`kdf` field and are handled by the legacy CryptoJS path in the callers.
	New blobs/files have `kdf: "argon2id"` plus `salt` and `argon2_params`.

	The `wallet_version` field is NOT used as a crypto-format discriminator; it
	keeps tracking key-derivation offset semantics (see UserContext.js).
*/

import { argon2id } from "hash-wasm"
import { getLog } from "./helpers"

const KDF_NAME = "argon2id"

// 64 MiB memory, 3 passes, 1 lane. Chosen for a desktop/web wallet: ~150-250ms
// on a modern machine, strong against GPU/ASIC bruteforce.
const DEFAULT_ARGON2_PARAMS = {
	m: 65536,        // memory in KiB (64 MiB)
	t: 3,            // iterations / passes
	p: 1,            // parallelism (lanes)
	type: "argon2id",
}

const SALT_LEN = 16     // bytes
const IV_LEN = 12       // bytes (AES-GCM standard nonce size)
const KEY_LEN = 32      // bytes (AES-256)
const GCM_TAG_LEN = 16  // bytes (AES-GCM auth tag)

// ---- base64 helpers (binary-string <-> base64, UTF-8 safe for mnemonic) ----

function bytesToBase64(bytes) {
	// bytes is a Uint8Array; build a binary string char-by-char.
	let bin = ""
	for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
	return btoa(bin)
}

function base64ToBytes(b64) {
	const bin = atob(b64)
	const out = new Uint8Array(bin.length)
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
	return out
}

function utf8ToBytes(str) {
	return new TextEncoder().encode(str)
}

function bytesToUtf8(bytes) {
	return new TextDecoder().decode(bytes)
}

function randomBytes(n) {
	const arr = new Uint8Array(n)
	crypto.getRandomValues(arr)
	return arr
}

// ---- Argon2id key derivation ----

async function deriveKey(password, salt, params) {
	// hash-wasm expects a password as a string (it utf8-encodes internally).
	return argon2id({
		password,
		salt,                      // Uint8Array
		parallelism: params.p,
		memorySize: params.m,      // KiB
		iterations: params.t,
		hashLength: KEY_LEN,        // bytes
		outputType: "binary",      // Uint8Array
	})
}

// ---- AES-GCM via Web Crypto ----

async function importAesGcmKey(rawKey) {
	return crypto.subtle.importKey(
		"raw",
		rawKey,
		{ name: "AES-GCM" },
		false,
		["encrypt", "decrypt"]
	)
}

/**
 * Encrypt a mnemonic with a password using Argon2id + AES-GCM.
 *
 * @param {string} mnemonic   the plaintext mnemonic (utf-8)
 * @param {string} password   the user's password
 * @returns {Promise<{
 *   kdf: "argon2id",
 *   salt: string,                       // base64, 16 bytes
 *   argon2_params: {m:number,t:number,p:number,type:string},
 *   encrypted_mnemonic: string          // base64: IV(12) || ciphertext || tag(16)
 * }>}
 */
export async function encryptMnemonic(mnemonic, password) {
	const log = getLog("wallet_crypto.encryptMnemonic(): ")
	if (!mnemonic) throw new Error("encryptMnemonic: mnemonic is required")
	if (!password) throw new Error("encryptMnemonic: password is required")

	const params = DEFAULT_ARGON2_PARAMS
	const salt = randomBytes(SALT_LEN)
	const iv = randomBytes(IV_LEN)

	const keyBytes = await deriveKey(password, salt, params)
	const key = await importAesGcmKey(keyBytes)

	const plaintext = utf8ToBytes(mnemonic)
	// subtle.encrypt for AES-GCM returns ciphertext || tag (tag is the last 16 bytes)
	const ctAndTag = new Uint8Array(
		await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext)
	)

	// pack: IV || ciphertext || tag
	const packed = new Uint8Array(IV_LEN + ctAndTag.length)
	packed.set(iv, 0)
	packed.set(ctAndTag, IV_LEN)

	return {
		kdf: KDF_NAME,
		salt: bytesToBase64(salt),
		argon2_params: { ...params },
		encrypted_mnemonic: bytesToBase64(packed),
	}
}

/**
 * Decrypt a mnemonic encrypted with encryptMnemonic.
 *
 * @param {{
 *   kdf?: string,
 *   salt?: string,
 *   argon2_params?: {m:number,t:number,p:number},
 *   encrypted_mnemonic: string
 * }} blob       the parsed backup/session blob
 * @param {string} password   the user's password
 * @returns {Promise<string|null>} the mnemonic, or null on wrong password
 *                                 (GCM auth failure) or tamper.
 */
export async function decryptMnemonic(blob, password) {
	const log = getLog("wallet_crypto.decryptMnemonic(): ")
	if (!blob || !blob.encrypted_mnemonic) {
		log("blob missing encrypted_mnemonic")
		return null
	}
	if (blob.kdf !== KDF_NAME) {
		// not an argon2id blob — caller should have routed to legacy path
		log("blob.kdf is not argon2id: ", blob.kdf)
		return null
	}
	if (!blob.salt || !blob.argon2_params) {
		log("blob missing salt/argon2_params")
		return null
	}
	if (!password) {
		log("no password provided")
		return null
	}

	try {
		const params = { ...DEFAULT_ARGON2_PARAMS, ...blob.argon2_params }
		// make sure type is set (older writers may have omitted it)
		if (!params.type) params.type = KDF_NAME

		const salt = base64ToBytes(blob.salt)
		const packed = base64ToBytes(blob.encrypted_mnemonic)

		if (packed.length < IV_LEN + GCM_TAG_LEN) {
			log("encrypted_mnemonic too short")
			return null
		}

		const iv = packed.slice(0, IV_LEN)
		const ctAndTag = packed.slice(IV_LEN) // ciphertext || tag

		const keyBytes = await deriveKey(password, salt, params)
		const key = await importAesGcmKey(keyBytes)

		const plaintext = await crypto.subtle.decrypt(
			{ name: "AES-GCM", iv },
			key,
			ctAndTag
		)
		return bytesToUtf8(new Uint8Array(plaintext))
	} catch (err) {
		// OperationError on wrong password / tamper — anything else is still
		// a "could not decrypt" => treat as wrong password for the UX.
		log("decrypt failed (wrong password or tamper): ", err && err.name, err && err.message)
		return null
	}
}

/**
 * Returns true if a parsed blob/file is in the new Argon2id format.
 * Used by callers to decide whether to route to decryptMnemonic or the
 * legacy CryptoJS path.
 *
 * @param {{kdf?: string}} blob
 * @returns {boolean}
 */
export function isArgon2Blob(blob) {
	return !!(blob && blob.kdf === KDF_NAME)
}
